import React, { useEffect, useRef, useState } from "react";
import { api, apiJson } from "@/api/client.js";
import { dayLabel } from "@/lib/dayLabels.js";
import { spotNumber } from "@/lib/spotNumbers.js";
import { GroupBoard } from "@/components/GroupBoard.jsx";
import { localStampClock } from "@/lib/huntDates.js";
import { rainWords, weatherFace, windSentence } from "@/lib/weatherFace.js";

export function planLine(teams) {
  const rows = Array.isArray(teams) ? teams : [];
  if (!rows.length) return "Pas encore décidé";
  const vehicles = rows.filter((row) => row.vehicle_id).length;
  const spots = rows.filter((row) => row.spot_name && !row.undecided).length;
  const word = (n, one, many) => `${n} ${n > 1 ? many : one}`;
  return `${word(rows.length, "équipe", "équipes")} · ${word(vehicles, "véhicule", "véhicules")} · ${word(spots, "secteur", "secteurs")}`;
}

export function PlanGlance({ day, onOpen, hunters = [] }) {
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    api(`/api/day-plan?day=${day}`).then((body) => {
      setPlan(body?.date ? body : { teams: [] });
    }).catch(() => setPlan({ teams: [] }));
  }, [day]);
  if (!plan) return <p className="og-hint">Plan du jour…</p>;
  const teams = plan.teams || [];
  const names = Object.fromEntries(hunters.map((row) => [row.id, row.first_name]));
  return (
    <div data-testid="day-plan">
      {teams.length ? (
        <ul className="og-teamlist" data-testid="plan-result">
          {teams.map((team, index) => (
            <li key={team.id}>
              <strong>Équipe {index + 1}</strong>
              <span>{team.hunters.map((id) => names[id] || id.replace("mem_", "")).join(" + ")}</span>
              <span>{(plan.vehicles || []).find((row) => row.id === team.vehicle_id)?.name || "Véhicule à choisir"}</span>
              <span>{team.undecided || !team.spot_name ? "À décider sur le terrain" : team.spot_name}</span>
            </li>
          ))}
        </ul>
      ) : <p data-testid="day-plan-empty">Pas encore décidé ce matin.</p>}
      <button type="button" className="og-btn og-btn--primary" data-testid="open-organize" onClick={onOpen}>Voir / modifier le plan</button>
    </div>
  );
}

const DEFAULT_VEHICLES = [
  { id: "veh_1", name: "4 roues #1", capacity: 2 },
  { id: "veh_2", name: "4 roues #2", capacity: 2 },
  { id: "veh_3", name: "6 roues", capacity: 2 },
  { id: "veh_4", name: "Side-by-side", capacity: 2 },
];

function seatsOf(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 2;
  return Math.max(1, Math.min(6, Math.round(number)));
}

function blankTeam(vehicleId) {
  return { id: `eq${vehicleId}`, hunters: [], vehicle_id: vehicleId, undecided: true, spot_id: "", spot_name: "", note: "" };
}

function withVehicles(rows) {
  const source = rows?.length === 4 ? rows : DEFAULT_VEHICLES;
  return DEFAULT_VEHICLES.map((fallback, index) => {
    const row = source[index] || fallback;
    return { id: fallback.id, name: row.name || fallback.name, capacity: seatsOf(row.capacity ?? fallback.capacity) };
  });
}

function withTeams(rows, vehicles) {
  return vehicles.map((vehicle) => {
    const found = (rows || []).find((team) => team.vehicle_id === vehicle.id);
    return found ? { ...blankTeam(vehicle.id), ...found, hunters: [...(found.hunters || [])] } : blankTeam(vehicle.id);
  });
}

export { nextSpotNumber, spotNumber, withSpotNumbers } from "@/lib/spotNumbers.js";

function weatherLine(weather) {
  if (!weather?.ok) return "Météo du jour pas encore reçue.";
  const noon = weather.moments?.noon || {};
  const face = weatherFace(noon.weather_code, false);
  const temp = Number(noon.temperature);
  const degrees = Number.isFinite(temp) ? `${Math.round(temp)}°` : "—";
  const sky = face.label ? face.label.toLowerCase() : "conditions reçues";
  return `Lever ${localStampClock(weather.sunrise)}, coucher ${localStampClock(weather.sunset)}. Midi : ${sky}, ${degrees}. ${windSentence(noon.wind_dir, noon.wind)}. ${rainWords(noon.rain ?? weather.daily?.rain)}.`;
}

export function DayTeams({ day, admin, hunters, onWeather }) {
  const [plan, setPlan] = useState({ date: day, teams: [], can_edit: admin });
  const [vehicles, setVehicles] = useState(DEFAULT_VEHICLES);
  const [draft, setDraft] = useState(withTeams([], DEFAULT_VEHICLES));
  const [spotNos, setSpotNos] = useState({});
  const [activeVehicle, setActiveVehicle] = useState("veh_1");
  const [weather, setWeather] = useState(null);
  const [hold, setHold] = useState("");
  const [fullMsg, setFullMsg] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const dirty = useRef(false);

  async function reload() {
    const body = await api(`/api/day-plan?day=${day}`);
    const nextPlan = body?.date ? body : { date: day, teams: [], can_edit: admin };
    const nextVehicles = withVehicles(nextPlan.vehicles);
    setPlan(nextPlan);
    if (!dirty.current) {
      setVehicles(nextVehicles);
      setDraft(withTeams(nextPlan.teams, nextVehicles));
    }
  }

  useEffect(() => {
    reload().catch(() => setPlan({ date: day, teams: [], can_edit: admin }));
    api(`/api/weather?day=${day}`).then((body) => { if (body?.ok) setWeather(body); }).catch(() => {});
  }, [day]);

  const editable = Boolean(admin && plan?.can_edit);
  const nameOf = (id) => hunters.find((hunter) => hunter.id === id)?.first_name || id;
  const label = dayLabel(day);

  function markDirty(nextDraft) {
    dirty.current = true;
    setSavedNote("");
    setDraft(nextDraft);
  }

  async function savePlan() {
    const vehiclePayload = vehicles.map((row) => ({ id: row.id, name: row.name, capacity: seatsOf(row.capacity) }));
    const teamPayload = draft
      .map((team) => ({ ...team, hunters: team.hunters.slice(0, seatsOf(vehicles.find((row) => row.id === team.vehicle_id)?.capacity)) }))
      .filter((team) => team.hunters.length);
    if (editable) {
      const savedVehicles = await apiJson("/api/terrain-vehicles", { vehicles: vehiclePayload }, "PUT");
      if (savedVehicles?.vehicles) setVehicles(withVehicles(savedVehicles.vehicles));
      const saved = await apiJson("/api/day-plan", { date: day, teams: teamPayload }, "PUT");
      if (saved?.date) {
        setPlan(saved);
        setDraft(withTeams(saved.teams, withVehicles(saved.vehicles || vehiclePayload)));
      }
    }
    dirty.current = false;
    setSavedNote("Plan enregistré.");
  }

  function renameVehicle(id, name) {
    const trimmed = name.trim();
    if (!trimmed) return;
    dirty.current = true;
    setVehicles((current) => current.map((row) => (row.id === id ? { ...row, name: trimmed } : row)));
  }

  function changeSeats(id, delta) {
    if (!editable) return;
    dirty.current = true;
    setSavedNote("");
    setVehicles((current) => current.map((row) => (row.id === id ? { ...row, capacity: seatsOf(seatsOf(row.capacity) + delta) } : row)));
    setDraft((current) => current.map((team) => {
      if (team.vehicle_id !== id) return team;
      const vehicle = vehicles.find((row) => row.id === id);
      const next = seatsOf(seatsOf(vehicle?.capacity) + delta);
      return { ...team, hunters: team.hunters.slice(0, next) };
    }));
  }

  function placeHunter(hunterId, vehicleId) {
    if (!editable || !hunterId || !vehicleId) return;
    const vehicle = vehicles.find((row) => row.id === vehicleId);
    const capacity = seatsOf(vehicle?.capacity);
    const occupied = draft.find((team) => team.vehicle_id === vehicleId) || blankTeam(vehicleId);
    const already = occupied.hunters.includes(hunterId);
    const others = occupied.hunters.filter((id) => id !== hunterId);
    if (!already && others.length >= capacity) {
      setFullMsg("Plus de place dans ce véhicule. Augmente les places si besoin.");
      return;
    }
    setFullMsg("");
    setActiveVehicle(vehicleId);
    markDirty(draft.map((team) => {
      const huntersLeft = team.hunters.filter((id) => id !== hunterId);
      if (team.vehicle_id !== vehicleId) return { ...team, hunters: huntersLeft };
      return { ...team, hunters: [...huntersLeft, hunterId].slice(0, capacity) };
    }));
    setHold("");
  }

  function removeHunter(hunterId) {
    markDirty(draft.map((team) => ({ ...team, hunters: team.hunters.filter((id) => id !== hunterId) })));
  }

  function assignSpot(point) {
    if (!editable || !point) return;
    if (point.number) setSpotNos((current) => ({ ...current, [point.id]: point.number }));
    setFullMsg("");
    markDirty(draft.map((team) => (
      team.vehicle_id === activeVehicle
        ? { ...team, undecided: false, spot_id: point.id, spot_name: point.name }
        : team
    )));
  }

  const preview = draft.filter((team) => team.hunters.length || (team.spot_name && !team.undecided));

  return (
    <div className="og-organize" data-testid="day-teams">
      <p className="og-wxbrief" data-testid="plan-weather">
        {weatherLine(weather)}
        <button type="button" className="og-textlink" data-testid="plan-weather-more" onClick={onWeather}>Voir météo</button>
      </p>
      <p data-testid="plan-line">{planLine(draft.filter((team) => team.hunters.length))}</p>

      <section data-testid="team-board">
          <h3>Chasseurs</h3>
          <div className="og-pick" data-testid="hunter-tray">
            {hunters.map((hunter) => (
              <button
                key={hunter.id}
                type="button"
                draggable={editable}
                className={`og-btn${hold === hunter.id ? " is-on" : ""}`}
                data-testid={`team-hunter-${hunter.id}`}
                onDragStart={(event) => {
                  event.dataTransfer.setData("text/plain", hunter.id);
                  setHold(hunter.id);
                }}
                onClick={() => setHold(hold === hunter.id ? "" : hunter.id)}
              >
                {hunter.first_name}
              </button>
            ))}
          </div>
          {fullMsg ? <p data-testid="vehicle-full">{fullMsg}</p> : null}
          <div className="og-vboard">
            {vehicles.map((vehicle) => {
              const crew = draft.find((team) => team.vehicle_id === vehicle.id);
              const people = crew?.hunters || [];
              const capacity = seatsOf(vehicle.capacity);
              const number = spotNos[crew?.spot_id] || spotNumber({ id: crew?.spot_id });
              const spot = crew?.undecided || !crew?.spot_name
                ? "Spot à choisir"
                : `${number ? `#${number} ` : ""}${crew.spot_name}`;
              return (
                <article
                  key={vehicle.id}
                  className={`og-vcard${people.length >= capacity ? " is-full" : ""}${activeVehicle === vehicle.id ? " is-picked" : ""}`}
                  data-testid={`team-vehicle-${vehicle.id}`}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    placeHunter(event.dataTransfer.getData("text/plain") || hold, vehicle.id);
                  }}
                  onClick={() => { if (hold) placeHunter(hold, vehicle.id); else setActiveVehicle(vehicle.id); }}
                >
                  <strong>{vehicle.name}</strong>
                  <span data-testid={`vehicle-count-${vehicle.id}`}>{people.length}/{capacity}</span>
                  <div className="og-seats">
                    <button type="button" data-testid={`vehicle-seats-minus-${vehicle.id}`} onClick={(event) => { event.stopPropagation(); changeSeats(vehicle.id, -1); }}>−</button>
                    <span data-testid={`vehicle-seats-${vehicle.id}`}>{capacity} places</span>
                    <button type="button" data-testid={`vehicle-seats-plus-${vehicle.id}`} onClick={(event) => { event.stopPropagation(); changeSeats(vehicle.id, 1); }}>+</button>
                  </div>
                  <ul>
                    {people.map((id) => (
                      <li key={id}>
                        {nameOf(id)}
                        <button type="button" data-testid={`team-remove-${id}`} onClick={(event) => { event.stopPropagation(); removeHunter(id); }}>Retirer</button>
                      </li>
                    ))}
                  </ul>
                  <p data-testid={`vehicle-spot-${vehicle.id}`}>{spot}</p>
                  <input className="og-input" aria-label={`Nom de ${vehicle.name}`} defaultValue={vehicle.name} onBlur={(event) => renameVehicle(vehicle.id, event.target.value)} />
                </article>
              );
            })}
          </div>
          <p className="og-hint">Propriétaires des véhicules : à confirmer. Les places se règlent sur chaque véhicule.</p>
        </section>

        <section data-testid="spot-picker">
          <h3>Spot du véhicule choisi</h3>
          <button type="button" className="og-btn" data-testid="spot-undecided" onClick={() => markDirty(draft.map((team) => (team.vehicle_id === activeVehicle ? { ...team, undecided: true, spot_id: "", spot_name: "" } : team)))}>À décider sur le terrain</button>
          <GroupBoard onAssign={assignSpot} />
        </section>

        <section className="og-planpreview" data-testid="plan-preview">
          <h3>Plan avant enregistrement</h3>
          {preview.length ? preview.map((team) => {
            const vehicle = vehicles.find((row) => row.id === team.vehicle_id);
            return (
              <p key={team.vehicle_id}>
                <strong>{vehicle?.name || "Véhicule"}</strong>
                {" · "}
                {team.hunters.length ? team.hunters.map(nameOf).join(", ") : "aucun chasseur"}
                {" · "}
                {team.undecided || !team.spot_name ? "spot à décider" : team.spot_name}
              </p>
            );
          }) : <p>Le plan est encore vide.</p>}
          {savedNote ? <p data-testid="plan-saved">{savedNote}</p> : null}
          {editable ? (
            <button type="button" className="og-btn og-btn--primary" data-testid="plan-save" onClick={savePlan}>
              {`Enregistrer le plan du ${label}`}
            </button>
          ) : null}
        </section>
    </div>
  );
}
